import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";import { Radio, PartyPopper } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const FAKE_USERNAMES = [
  "lena.dance_", "max_travel22", "sophie.creative", "julien_fitness", "chloe.beauty",
  "thomas_moto", "manon.cuisine", "hugo_gaming", "clara.photo", "nicolas.music", "emma_yoga", "lucas.tech",
];
const FAKE_BUYERS = [
  ["Lina", "Angers"], ["Karim", "Lyon"], ["Sarah", "Marseille"], ["Thomas", "Lille"], ["Camille", "Nantes"],
  ["Julien", "Toulouse"], ["Manon", "Bordeaux"], ["Nicolas", "Strasbourg"], ["Léa", "Rennes"], ["Antoine", "Nice"],
  ["Chloé", "Paris"], ["Maxime", "Montpellier"], ["Inès", "Genève"], ["David", "Bruxelles"], ["Sabrina", "Montréal"],
];
const QTY_POOL = [100, 250, 500, 1000, 2500, 5000];

function getSocialProofAction(category) {
  const c = (category || "").toLowerCase();
  if (c.startsWith("likes") || c.startsWith("combo")) return { verb_fr: "a aimé votre publication", verb_en: "liked your post", label_fr: "Likes", label_en: "Likes" };
  if (c.startsWith("reactions")) return { verb_fr: "a réagi à votre publication", verb_en: "reacted to your post", label_fr: "Réactions", label_en: "Reactions" };
  if (c.startsWith("comments")) return { verb_fr: "a commenté votre publication", verb_en: "commented on your post", label_fr: "Commentaires", label_en: "Comments" };
  if (c.startsWith("views") || c.startsWith("video_views") || c.startsWith("viewers")) return { verb_fr: "a regardé votre vidéo", verb_en: "watched your video", label_fr: "Vues", label_en: "Views" };
  if (c.startsWith("shares")) return { verb_fr: "a partagé votre publication", verb_en: "shared your post", label_fr: "Partages", label_en: "Shares" };
  if (c.startsWith("group_members") || c.startsWith("members")) return { verb_fr: "a rejoint votre groupe", verb_en: "joined your group", label_fr: "Membres", label_en: "Members" };
  if (c.startsWith("streams") || c.startsWith("plays")) return { verb_fr: "a écouté votre son", verb_en: "listened to your track", label_fr: "Streams", label_en: "Streams" };
  if (c.startsWith("repins")) return { verb_fr: "a repin votre image", verb_en: "repinned your image", label_fr: "Repins", label_en: "Repins" };
  if (c.startsWith("connections")) return { verb_fr: "s'est connecté(e) avec vous", verb_en: "connected with you", label_fr: "Connexions", label_en: "Connections" };
  return { verb_fr: "a commencé à vous suivre", verb_en: "started following you", label_fr: "Followers", label_en: "Followers" };
}

export default function LiveSocialProof({ category, networkName, trustCount, accentColor }) {
  const { language, t } = useLanguage();
  const action = getSocialProofAction(category);
  const [followIdx, setFollowIdx] = useState(0);
  const [followMin, setFollowMin] = useState(1);
  const [buyIdx, setBuyIdx] = useState(0);
  const [buyMin, setBuyMin] = useState(12);
  const [buyQty, setBuyQty] = useState(QTY_POOL[2]);

  useEffect(() => {
    const t1 = setInterval(() => {
      setFollowIdx((i) => (i + 1) % FAKE_USERNAMES.length);
      setFollowMin(1 + Math.floor(Math.random() * 8));
    }, 4200);
    const t2 = setInterval(() => {
      setBuyIdx((i) => (i + 1) % FAKE_BUYERS.length);
      setBuyMin(2 + Math.floor(Math.random() * 40));
      setBuyQty(QTY_POOL[Math.floor(Math.random() * QTY_POOL.length)]);
    }, 6500);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  const username = FAKE_USERNAMES[followIdx];
  const [buyerName, buyerCity] = FAKE_BUYERS[buyIdx];
  const verb = language === "en" ? action.verb_en : action.verb_fr;
  const label = language === "en" ? action.label_en : action.label_fr;

  return (
    <div className="max-w-4xl mx-auto mb-10 space-y-3" data-testid="live-social-proof">
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}
        className="rounded-2xl bg-zinc-950 border border-white/10 px-5 py-3 flex items-center justify-center gap-3 text-center" data-testid="trust-banner"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <p className="text-sm sm:text-base font-semibold text-white">
          {t("network.trust_banner_prefix", "Plus de")} <span style={{ color: accentColor }}>{trustCount.toLocaleString(language === "en" ? "en-US" : "fr-FR")}+</span> {t("network.trust_banner_suffix", "personnes nous font déjà confiance pour")} {networkName}
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="relative rounded-2xl bg-zinc-900/60 border border-white/8 px-4 py-3 overflow-hidden" data-testid="follow-preview-widget">
          <span className="absolute top-2.5 right-3 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400">
            <Radio className="w-3 h-3 animate-pulse" /> {t("network.live_preview_badge", "Aperçu en direct")}
          </span>
          <AnimatePresence mode="wait">
            <motion.div
              key={username} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.35 }}
              className="flex items-center gap-3 pr-16"
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: `linear-gradient(135deg, ${accentColor}, #a855f7)` }}>
                {username.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 truncate"><span className="font-semibold text-white">{username}</span> {verb}</p>
                <p className="text-[11px] text-zinc-500">{followMin} min</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative rounded-2xl bg-zinc-900/60 border border-white/8 px-4 py-3 overflow-hidden" data-testid="purchase-notification-widget">
          <AnimatePresence mode="wait">
            <motion.div
              key={buyIdx} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.35 }}
              className="flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accentColor}22` }}>
                <PartyPopper className="w-4 h-4" style={{ color: accentColor }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 truncate">
                  <span className="font-semibold text-white">{buyerName}</span> {t("network.live_from", "de")} {buyerCity} {t("network.live_bought", "a acheté")} {buyQty.toLocaleString(language === "en" ? "en-US" : "fr-FR")} {label}
                </p>
                <p className="text-[11px] text-zinc-500">{buyMin} min</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
